/**
 * Focus return without a stray ring.
 *
 * Chrome keeps `:focus-visible` on a programmatic `focus()` when the element
 * that had focus matched it, and a text field always matches it. So a dialog
 * that held a text field lit up its trigger on every close, even after a
 * pointer interaction. The module tracks the last input modality and marks a
 * pointer-driven return with `data-focus-return`, which the global stylesheet
 * turns into "no ring". Only keys that move or activate focus count as
 * keyboard input; Escape and typing do not change the modality.
 */

export type InputModality = "pointer" | "keyboard";

const FOCUS_KEYS = new Set([
  "Tab",
  "ArrowUp",
  "ArrowDown",
  "ArrowLeft",
  "ArrowRight",
  "Home",
  "End",
  "Enter",
  " ",
]);

let modality: InputModality = "keyboard";
let installed = false;

function install(): void {
  if (installed || typeof window === "undefined") return;
  installed = true;
  window.addEventListener(
    "keydown",
    (event) => {
      if (FOCUS_KEYS.has(event.key)) modality = "keyboard";
    },
    true,
  );
  window.addEventListener(
    "pointerdown",
    () => {
      modality = "pointer";
    },
    true,
  );
}

/** The modality of the most recent focus-moving interaction. */
export function lastInputModality(): InputModality {
  install();
  return modality;
}

// Listen from module load. Installing lazily on the first focus return would
// miss the very click that opened the overlay, and the first return after
// page load would always read as keyboard.
install();

/**
 * Focus `element` as the target of an overlay's focus return. After a
 * pointer interaction the element carries `data-focus-return` until it
 * blurs, so no ring paints.
 */
export function returnFocus(element: HTMLElement): void {
  install();
  element.focus({ preventScroll: true });
  if (modality !== "pointer") return;
  element.setAttribute("data-focus-return", "");
  element.addEventListener(
    "blur",
    () => {
      element.removeAttribute("data-focus-return");
    },
    { once: true },
  );
}
